import { formatDateString } from "@/v2-common/utils/utils.date"
import type { DotProps } from "@/v2-backoffice-client/components/dot"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import type {
  CustomerMergeActionFragment,
  CustomerMergeFragment,
  CustomerMergeHoldingsFragment,
  CustomerMergeSideFragment,
} from "@/v2-backoffice-client/customer/merge/__types__/customer.merge.fragment"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"

/**
 * What HubSpot did to a customer's company, as the Backoffice reads it off
 * Customer.MergedInto (acc.CustomerMergeLeftovers): the CRM merged the
 * company into another one this customer does not hold, so the row is what
 * the merge left behind. Its teams and users belong on the customer holding
 * the survivor - the Backoffice moves them there on its own when the money
 * side is clear (Customers → CRM Merges) - and nothing should be put on it
 * again. One wording for the pickers, the CRM badge and the customer
 * panel's notice.
 */
export const CUSTOMER_MERGE_RULE =
  "HubSpot merged this customer's company into another company. The row is what the merge left behind: its teams and users belong on the surviving customer (Customers → CRM Merges moves them, or shows what blocks it), and nothing new should be put on it."

export type CustomerMergedInto = NonNullable<CustomerFragment["MergedInto"]>

/** A customer row reduced to what the merge wording needs. */
export type MergedCustomer = Pick<CustomerFragment, "MergedInto">

/** "Bryn Eiendom AS (55470946673)", or just the company id until the sync has linked a customer to it. */
export function formatMergeSurvivor(mergedInto: CustomerMergedInto) {
  const survivor = mergedInto.SurvivingCustomer
  if (!survivor)
    return `HubSpot company ${mergedInto.SurvivingExternalCompanyId}`
  return `${survivor.Name ?? "Unnamed"} (${mergedInto.SurvivingExternalCompanyId})`
}

/**
 * The one-line reason a picker shows under a leftover row instead of
 * letting it be picked: where the merge went, and that the survivor is the
 * row to pick - or that nobody holds it yet. Null for a customer HubSpot
 * still has, and for a leftover an operator kept as a separate customer:
 * that one is an ordinary customer again.
 */
export function describeMergedInto(customer: MergedCustomer) {
  const mergedInto = customer.MergedInto
  if (!mergedInto || mergedInto.IsKeptSeparate) return null
  if (!mergedInto.SurvivingCustomer) {
    return `Merged in HubSpot into company ${mergedInto.SurvivingExternalCompanyId}; the sync links its customer within the hour`
  }
  return `Merged in HubSpot into ${formatMergeSurvivor(mergedInto)} — pick that one`
}

// ---------------------------- The CRM Merges board ---------------------------- //

// The GraphQL CustomerMergeStatus / CustomerMergeBlocker /
// CustomerMergeActionCode enums as they reach the client (string values),
// kept as literals like the other status constants so call sites don't
// import the generated enum objects at runtime; the maps below are keyed
// by string for the same reason (userContact.constants does the same).

export const CUSTOMER_MERGE_STATUS_PENDING = "PENDING"
export const CUSTOMER_MERGE_STATUS_NEEDS_DECISION = "NEEDS_DECISION"
export const CUSTOMER_MERGE_STATUS_IN_PROGRESS = "IN_PROGRESS"
export const CUSTOMER_MERGE_STATUS_AWAITING_SYNC = "AWAITING_SYNC"
export const CUSTOMER_MERGE_STATUS_KEPT_SEPARATE = "KEPT_SEPARATE"
export const CUSTOMER_MERGE_STATUS_SETTLED = "SETTLED"

export const CUSTOMER_MERGE_BLOCKER_BOTH_HOLD_MONEY = "BOTH_HOLD_MONEY"
export const CUSTOMER_MERGE_BLOCKER_EMPTIED_HOLDS_MONEY = "EMPTIED_HOLDS_MONEY"
export const CUSTOMER_MERGE_BLOCKER_EMPTIED_HOLDS_CONFIG =
  "EMPTIED_HOLDS_CONFIG"
export const CUSTOMER_MERGE_BLOCKER_KEEPER_HAS_NO_KEY = "KEEPER_HAS_NO_KEY"

export const CUSTOMER_MERGE_ACTION_CONSOLIDATED = "CONSOLIDATED"
export const CUSTOMER_MERGE_ACTION_KEPT_SEPARATE = "KEPT_SEPARATE"

/** The statuses that still want something from someone: the tab's count. */
export const CUSTOMER_MERGE_OPEN_STATUSES: readonly string[] = [
  CUSTOMER_MERGE_STATUS_NEEDS_DECISION,
  CUSTOMER_MERGE_STATUS_PENDING,
  CUSTOMER_MERGE_STATUS_IN_PROGRESS,
  CUSTOMER_MERGE_STATUS_AWAITING_SYNC,
]

/** Every status, in the board's order (decisions first, settled last). */
export const CUSTOMER_MERGE_STATUSES: readonly string[] = [
  ...CUSTOMER_MERGE_OPEN_STATUSES,
  CUSTOMER_MERGE_STATUS_KEPT_SEPARATE,
  CUSTOMER_MERGE_STATUS_SETTLED,
]

type StatusDisplay = {
  label: string
  variant: DotProps["variant"]
  /** What the status means, for the column tooltip and the row. */
  description: string
}

// One label + dot colour + sentence per status: the board's Status column,
// its grouping values and the customer panel's notice all read this.
const STATUS_DISPLAY: Record<string, StatusDisplay> = {
  [CUSTOMER_MERGE_STATUS_NEEDS_DECISION]: {
    label: "Needs a decision",
    variant: "destructive",
    description:
      "Both rows hold something the Backoffice cannot move on its own. Clear the blocker and the merge heals by itself, or keep the rows separate.",
  },
  [CUSTOMER_MERGE_STATUS_PENDING]: {
    label: "Consolidating",
    variant: "orange",
    description:
      "The Backoffice moves the people onto the keeper within minutes while someone is using it. The filled button does it at once; or pick the other keeper, or keep the rows separate.",
  },
  [CUSTOMER_MERGE_STATUS_IN_PROGRESS]: {
    label: "In progress",
    variant: "warning",
    description:
      "An action was written and is being carried out; it finishes within minutes. A run that died is finished by the next one.",
  },
  [CUSTOMER_MERGE_STATUS_AWAITING_SYNC]: {
    label: "Awaiting the sync",
    variant: "muted",
    description:
      "The surviving company has no customer with content yet; the hourly CRM sync links or creates one, or retires the empty row holding it.",
  },
  [CUSTOMER_MERGE_STATUS_KEPT_SEPARATE]: {
    label: "Kept separate",
    variant: "info",
    description:
      "An operator decided the row the merge left behind stays a customer of its own, without a CRM company.",
  },
  [CUSTOMER_MERGE_STATUS_SETTLED]: {
    label: "Settled",
    variant: "success-1",
    description:
      "Nothing is left to move: the people are on one row, or nothing was left behind.",
  },
}

// Unknown codes (from a newer backend) render as themselves, never blank.
export function getCustomerMergeStatusDisplay(status: string): StatusDisplay {
  return (
    STATUS_DISPLAY[status] ?? {
      label: status,
      variant: "muted",
      description: "",
    }
  )
}

/** The blocker in the operator's words, with what clears it. */
export function describeMergeBlocker(blocker: string | null | undefined) {
  switch (blocker) {
    case CUSTOMER_MERGE_BLOCKER_BOTH_HOLD_MONEY:
      return "Both rows hold a licence or a billing account. End or relink one of them (Licensing, Billing) and the merge heals on its own."
    case CUSTOMER_MERGE_BLOCKER_EMPTIED_HOLDS_MONEY:
      return "The row that would be emptied holds a licence or a billing account. Keep that row instead, or end the licence first."
    case CUSTOMER_MERGE_BLOCKER_EMPTIED_HOLDS_CONFIG:
      return "The row that would be emptied holds an add-on, an access list, a closed feature class or a collections owner, which have nowhere to go. Move or clear them on that customer first."
    case CUSTOMER_MERGE_BLOCKER_KEEPER_HAS_NO_KEY:
      return "The keeper has no legacy HubSpot id to move people under; its company link needs relinking."
    default:
      return null
  }
}

export function formatMergeSide(
  side: CustomerMergeSideFragment | null | undefined,
) {
  if (!side) return "no customer"
  return side.Name ?? `customer #${side.Id}`
}

/**
 * "1 team, 3 users" / "a licence, a billing account, 2 users" - what a row
 * holds, people first then money then config, nothing for an empty row.
 */
export function describeHoldings(
  holdings: CustomerMergeHoldingsFragment | null | undefined,
) {
  if (!holdings) return "nothing"
  const parts = [
    plural(holdings.Tenants, "team"),
    plural(holdings.Users, "user"),
    plural(holdings.Licenses, "licence"),
    plural(holdings.BillingAccounts, "billing account"),
    holdings.Config > 0
      ? `${holdings.Config} config item${holdings.Config === 1 ? "" : "s"}`
      : null,
  ].filter((part): part is string => part !== null)
  return parts.length > 0 ? parts.join(", ") : "nothing"
}

/** Whether a row holds a licence or a billing account: the keeper rule. */
export function holdsMoney(
  holdings: CustomerMergeHoldingsFragment | null | undefined,
) {
  return !!holdings && holdings.Licenses + holdings.BillingAccounts > 0
}

function plural(count: number, noun: string) {
  if (count <= 0) return null
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

/** "automatically" or "by Kari Nordmann". */
export function describeActor(action: CustomerMergeActionFragment) {
  if (!action.ActorUserId) return "automatically"
  const name = formatUserName(action.Actor?.FirstName, action.Actor?.LastName)
  return `by ${name || action.Actor?.Email || `user #${action.ActorUserId}`}`
}

/**
 * One sentence for the newest action: "Moved 1 team and 3 users to Bryn
 * Eiendom AS on 02.11.2026 automatically" / "Kept separate by Kari Nordmann
 * on …; 1 team moved back". An incomplete action says so.
 */
export function describeMergeAction(action: CustomerMergeActionFragment) {
  const when = formatDateString(action.CreatedAt)
  const moved = [
    plural(action.TenantIds.length, "team"),
    plural(action.UserIds.length, "user"),
  ]
    .filter((part): part is string => part !== null)
    .join(" and ")
  const link = action.MovesLink ? " and the company link" : ""
  const incomplete = action.CompletedAt
    ? ""
    : action.LastError
      ? ` — not finished yet: ${action.LastError}`
      : " — being carried out"

  const code: string = action.Code
  if (code === CUSTOMER_MERGE_ACTION_KEPT_SEPARATE) {
    const back =
      moved || action.MovesLink
        ? `; ${moved || "nothing"}${link} moved back to ${formatMergeSide(action.ToCustomer)}`
        : ""
    return `Kept separate ${describeActor(action)} on ${when}${back}${incomplete}`
  }
  return `Moved ${moved || "nothing"}${link} from ${formatMergeSide(action.FromCustomer)} to ${formatMergeSide(action.ToCustomer)} on ${when} ${describeActor(action)}${incomplete}`
}

/**
 * The board's one line per merge: what happened or what is about to
 * happen, in the status's terms.
 */
export function describeMerge(merge: CustomerMergeFragment) {
  const status: string = merge.Status
  switch (status) {
    case CUSTOMER_MERGE_STATUS_SETTLED:
      if (merge.LatestAction) return describeMergeAction(merge.LatestAction)
      if (merge.IsRepointed)
        return "The customer's link moved onto the surviving company; nothing was left behind."
      if (!merge.LeftoverCustomer)
        return "Nothing was left behind: the sync retired the empty row, or we never had one."
      return "The row left behind is empty; nothing to move."
    case CUSTOMER_MERGE_STATUS_PENDING: {
      const keeperIsLeftover =
        merge.KeeperCustomer?.Id === merge.LeftoverCustomer?.Id
      const keeperHoldings = keeperIsLeftover
        ? merge.LeftoverHoldings
        : merge.SurvivorHoldings
      const emptiedHoldings = keeperIsLeftover
        ? merge.SurvivorHoldings
        : merge.LeftoverHoldings
      const why = holdsMoney(keeperHoldings)
        ? "it holds the money"
        : "HubSpot kept its company"
      const link = merge.MovesLink ? ", and the company link follows" : ""
      return `${formatMergeSide(merge.KeeperCustomer)} keeps (${why}); ${describeHoldings(emptiedHoldings)} of ${formatMergeSide(merge.EmptiedCustomer)} move onto it${link}.`
    }
    case CUSTOMER_MERGE_STATUS_NEEDS_DECISION:
      return (
        describeMergeBlocker(merge.Blocker) ??
        getCustomerMergeStatusDisplay(status).description
      )
    case CUSTOMER_MERGE_STATUS_IN_PROGRESS:
      return merge.LatestAction
        ? describeMergeAction(merge.LatestAction)
        : getCustomerMergeStatusDisplay(status).description
    case CUSTOMER_MERGE_STATUS_KEPT_SEPARATE:
      return merge.LatestAction
        ? describeMergeAction(merge.LatestAction)
        : getCustomerMergeStatusDisplay(status).description
    default:
      return getCustomerMergeStatusDisplay(status).description
  }
}
